import { Head, useForm, usePage } from "@inertiajs/react"

import PartnerAuthLogo, {
  type PartnerBranding,
} from "@/components/partner-auth-logo"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import AuthLayout from "@/layouts/auth-layout"
import { cn } from "@/lib/utils"
import { legalPrivacyPath, legalTermsPath } from "@/routes"

export default function Invitation() {
  const { invitation_token: token, partner } = usePage<{
    invitation_token: string
    partner: PartnerBranding | null
  }>().props
  const { data, setData, patch, processing, errors } = useForm({
    user: {
      name: "",
      password: "",
      password_confirmation: "",
      invitation_token: token,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch("/users/invitation", {
      onSuccess: () => {
        // reset the form
        setData("user.name", "")
        setData("user.password", "")
        setData("user.password_confirmation", "")
      },
    })
  }

  function handleGoogleLogin() {
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    )?.content
    const form = document.createElement("form")
    form.method = "POST"
    form.action = "/users/auth/google_oauth2"

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = csrfToken || ""

    form.appendChild(csrfInput)
    document.body.appendChild(form)
    form.submit()
  }

  const title = `Welcome to ${partner?.name ?? "Imperative"}`
  const description = "Create an account to join your team"

  return (
    <AuthLayout title="" description="">
      <Head title={title} />
      <div className={cn("flex w-full flex-col gap-6")}>
        <Card>
          <CardHeader className="text-center">
            <PartnerAuthLogo partner={partner} />
            <CardTitle className="text-xl">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="name">Name</FieldLabel>
                  <Input
                    id="name"
                    type="text"
                    name="user[name]"
                    placeholder="Enter your name"
                    value={data.user.name}
                    onChange={(e) =>
                      setData("user", { ...data.user, name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                  {errors["user.name"] && (
                    <FieldError>{errors["user.name"]}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    name="user[password]"
                    placeholder="Enter your password"
                    value={data.user.password}
                    onChange={(e) =>
                      setData("user", {
                        ...data.user,
                        password: e.target.value,
                      })
                    }
                    required
                  />
                  {errors["user.password"] && (
                    <FieldError>{errors["user.password"]}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="password_confirmation">
                    Password Confirmation
                  </FieldLabel>
                  <Input
                    id="password_confirmation"
                    type="password"
                    name="user[password_confirmation]"
                    placeholder="Confirm your password"
                    value={data.user.password_confirmation}
                    onChange={(e) =>
                      setData("user", {
                        ...data.user,
                        password_confirmation: e.target.value,
                      })
                    }
                    required
                  />
                  {errors["user.password_confirmation"] && (
                    <FieldError>
                      {errors["user.password_confirmation"]}
                    </FieldError>
                  )}
                </Field>
                <Field>
                  <Button
                    type="submit"
                    className="flex w-full items-center justify-center"
                    loading={processing}
                  >
                    {processing ? "Sending..." : "Continue"}
                  </Button>
                </Field>
                <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                  Or
                </FieldSeparator>
                <Field>
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full"
                    onClick={handleGoogleLogin}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="mr-2 h-5 w-5"
                      fill="none"
                    >
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continue with Google
                  </Button>
                  {/* IMP-266: record agreement to legal terms at account activation */}
                  <p className="text-muted-foreground text-center text-xs text-balance">
                    By continuing, you agree to the{" "}
                    <a
                      href={legalTermsPath()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href={legalPrivacyPath()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </Field>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  )
}
